import clsx from 'clsx'
import { html, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { DdsElement } from '../../internal/dds-hu-element'
import { SizedMixin } from '../../internal/mixin/sizedMixin'
import styles from './scroll-progress.scss?inline'

const ScrollProgressBaseWithSized = SizedMixin(DdsElement)

export type ScrollProgressVariant =
  | 'neutral'
  | 'brand'
  | 'negative'
  | 'positive'
  | 'inverted'

/**
 * `dap-ds-scroll-progress`
 * @summary A progress bar that displays scroll position, supporting both page-level and container-level scroll tracking.
 * @element dap-ds-scroll-progress
 * @title - Scroll Progress
 *
 * @csspart base - The main scroll progress container.
 * @csspart track - The progress track (background).
 * @csspart fill - The progress fill bar.
 *
 * @cssprop --dds-scroll-progress-z-index - Z-index for fixed positioning (default: 1000)
 * @cssprop --dds-scroll-progress-transition - Transition for progress updates (default: width 0.1s ease-out)
 * @cssprop --dds-scroll-progress-track-color - Background color of the progress track (default: var(--dds-neutral-200))
 * @cssprop --dds-scroll-progress-fill-color-neutral - Fill color for neutral variant
 * @cssprop --dds-scroll-progress-fill-color-brand - Fill color for brand variant
 * @cssprop --dds-scroll-progress-fill-color-negative - Fill color for negative variant
 * @cssprop --dds-scroll-progress-fill-color-positive - Fill color for positive variant
 * @cssprop --dds-scroll-progress-fill-color-inverted - Fill color for inverted variant
 *
 * @property { 'xxs' | 'xs' | 'sm' | 'md' | 'lg' } size - The size of the scroll progress bar. Default is `sm`. See SizedMixin.
 * @property {string} sizeMap - Responsive size map (e.g. "md:lg"); see SizedMixin.
 */
@customElement('dap-ds-scroll-progress')
export default class DapDSScrollProgress extends ScrollProgressBaseWithSized {
  /**
   * CSS selector for scroll container. If not set, tracks global page scroll.
   * When set, the component uses sticky positioning within the container.
   */
  @property({ type: String, reflect: true }) target?: string

  /**
   * The color variant of the progress indicator.
   * @type {"neutral" | "brand" | "negative" | "positive" | "inverted"}
   */
  @property({ type: String, reflect: true }) variant: ScrollProgressVariant =
    'brand'

  /**
   * Current progress value (0-100).
   */
  @state() private progress = 0

  private targetElement: Element | null = null
  private rafId: number | null = null
  private boundHandleScroll: () => void

  static readonly styles = unsafeCSS(styles)

  /** Maps effectiveSize from SizedMixin to scroll-progress visual size (xs, sm, md; xxs→xs, lg→md). */
  private get scrollProgressSize(): string {
    if (this.effectiveSize === 'xxs') return 'xs'
    if (this.effectiveSize === 'lg') return 'md'
    return this.effectiveSize
  }

  constructor() {
    super()
    this.boundHandleScroll = this.handleScroll.bind(this)
  }

  connectedCallback(): void {
    super.connectedCallback()
    this.setupScrollListener()
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    this.cleanupScrollListener()
  }

  protected updated(changedProperties: Map<PropertyKey, unknown>): void {
    super.updated(changedProperties)
    if (changedProperties.has('target')) {
      this.cleanupScrollListener()
      this.setupScrollListener()
    }
  }

  private setupScrollListener(): void {
    if (this.target) {
      this.targetElement = document.querySelector(this.target)
      if (this.targetElement) {
        this.targetElement.addEventListener('scroll', this.boundHandleScroll, {
          passive: true,
        })
      }
    } else {
      window.addEventListener('scroll', this.boundHandleScroll, {
        passive: true,
      })
    }
    // Initial calculation
    this.handleScroll()
  }

  private cleanupScrollListener(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }

    if (this.targetElement) {
      this.targetElement.removeEventListener('scroll', this.boundHandleScroll)
      this.targetElement = null
    } else {
      window.removeEventListener('scroll', this.boundHandleScroll)
    }
  }

  private handleScroll = (): void => {
    if (this.rafId !== null) {
      return
    }

    this.rafId = requestAnimationFrame(() => {
      this.calculateProgress()
      this.rafId = null
    })
  }

  private calculateProgress(): void {
    let scrollTop: number
    let scrollHeight: number
    let clientHeight: number

    if (this.target && this.targetElement) {
      scrollTop = this.targetElement.scrollTop
      scrollHeight = this.targetElement.scrollHeight
      clientHeight = this.targetElement.clientHeight
    } else {
      scrollTop = window.scrollY
      scrollHeight = document.documentElement.scrollHeight
      clientHeight = window.innerHeight
    }

    const scrollableHeight = scrollHeight - clientHeight

    if (scrollableHeight <= 0) {
      this.progress = 0
      return
    }

    this.progress = Math.min(
      100,
      Math.max(0, (scrollTop / scrollableHeight) * 100),
    )
  }

  render() {
    return html`
      <div
        part="base"
        class=${clsx(
          'scroll-progress',
          `scroll-progress--${this.variant}`,
          `scroll-progress--${this.scrollProgressSize}`,
        )}
        role="progressbar"
        aria-valuenow=${Math.round(this.progress)}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-label=${this.ariaLabel ?? 'Scroll progress'}>
        <div part="track" class="scroll-progress__track">
          <div
            part="fill"
            class="scroll-progress__fill"
            style="width: ${this.progress}%"></div>
        </div>
      </div>
    `
  }
}
